import type { Perfil } from '@/shared/api/auth';
import type { CountryCode } from '@/shared/countries';

/**
 * Permission helpers for the Tesorería module. The predicates here are
 * mirrored verbatim on the server at
 * `server/src/lib/tesoreriaPermissions.ts` — keep both in sync.
 *
 * Single source of truth for who can write what on which caja:
 *
 *   AR (every non-Nova country):
 *     - admin + administracion → write everything
 *     - everyone else → read-only
 *
 *   MX:
 *     - admin + administracion → write everything, Nova-channel cajas
 *       (Nova Banorte / Nova Mercado Pago / Herconstru) y Caja
 *       Diferencia incluidas.
 *     - nova → Nova-channel cajas + Caja Diferencia (los retiros
 *       informales del socio necesitan quedar registrados). Read-only
 *       en el resto.
 *     - everyone else → read-only
 *
 *   El gate de las cajas Nova cambió tres veces: 2026-05-18 se le quitó
 *   a `admin`, 2026-08-19 se le dio a `administracion`, y 2026-08-20 la
 *   dirección resolvió que los tres perfiles operen igual. El aviso que
 *   explicaba el bloqueo sigue vivo en TesoreriaPage por si se decide
 *   restringir de nuevo.
 *
 * "Nova caja" matches the same predicate as `isNovaChannelMedio` in
 * `salesPaymentReceipts.ts`: starts-with `'Nova '` or exactly
 * `'Herconstru'`.
 */

export function isNovaCaja(name: string | null | undefined): boolean {
  const m = (name ?? '').trim();
  if (!m) return false;
  if (m.startsWith('Nova ')) return true;
  if (m === 'Herconstru') return true;
  return false;
}

export function isCajaDiferencia(name: string | null | undefined): boolean {
  return (name ?? '').trim() === 'Caja Diferencia';
}

export function canWriteCaja(
  caja: string | null | undefined,
  perfil: Perfil | null | undefined,
  country: CountryCode,
): boolean {
  if (!perfil) return false;
  if (country !== 'MX') {
    // AR (and any future single-country deployment): admin-tier only,
    // no per-caja gating.
    return perfil === 'admin' || perfil === 'administracion';
  }
  // MX from here on. Cajas Nova + Caja Diferencia (auto-escrita por
  // `emitCajaDiferencia` con el margen y la comisión del 5%, más los
  // retiros manuales de Nova): los tres perfiles que llegan a Tesorería
  // las operan por igual.
  if (isNovaCaja(caja) || isCajaDiferencia(caja)) {
    return perfil === 'admin' || perfil === 'administracion' || perfil === 'nova';
  }
  return perfil === 'admin' || perfil === 'administracion';
}
